import { useState, useMemo, useRef, useLayoutEffect, forwardRef } from 'react';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import {
  ChevronRight,
  Edit3,
  Trash2,
  FolderPlus,
  ListPlus,
  Copy,
  Move,
  Archive,
  Settings,
  Plus,
  FileText,
  MessageSquare,
  Mic,
  Table,
  Presentation as PresentationIcon,
} from 'lucide-react';
import { useClickOutside } from '@/hooks/useClickOutside';
import clsx from 'clsx';

// Context menu types
export type ContextMenuType = 'space' | 'folder' | 'list' | 'item';

export interface ContextMenuAction {
  type:
    | 'rename'
    | 'delete'
    | 'add-folder'
    | 'add-list'
    | 'add-document'
    | 'add-chat'
    | 'add-voice-channel'
    | 'add-spreadsheet'
    | 'add-presentation'
    | 'duplicate'
    | 'move'
    | 'move-to-folder'
    | 'archive'
    | 'settings';
  spaceName: string;
  folderName?: string;
  listName?: string;
  itemId?: string;
  itemType?: string;
}

// Project Context Menu Component
interface ProjectContextMenuProps {
  x: number;
  y: number;
  type: ContextMenuType;
  spaceName: string;
  folderName?: string;
  listName?: string;
  itemId?: string;
  itemType?: string;
  itemName?: string;
  onAction: (action: ContextMenuAction['type']) => void;
  onClose: () => void;
}

export const ProjectContextMenu = forwardRef<HTMLDivElement, ProjectContextMenuProps>(
  ({ x, y, type, spaceName, folderName, listName, itemId: _itemId, itemType, itemName, onAction, onClose }, ref) => {
    const [showAddSubmenu, setShowAddSubmenu] = useState(false);
    const submenuRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState({ left: x, top: y });

    useLayoutEffect(() => {
      const el = menuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos(clampToViewport(x, y, rect.width, rect.height));
    }, [x, y]);

    const getTitle = () => {
      if (type === 'item') return itemName;
      if (type === 'list') return listName;
      if (type === 'folder') return folderName;
      return spaceName;
    };

    const itemTypeLabelMap: Record<string, string> = {
      document: 'Documento',
      chat: 'Chat',
      voice_channel: 'Canale vocale',
      spreadsheet: 'Foglio di calcolo',
      presentation: 'Presentazione',
    };

    const getTypeLabel = () => {
      if (type === 'item') return itemTypeLabelMap[itemType || ''] || 'Elemento';
      if (type === 'list') return 'Lista';
      if (type === 'folder') return 'Cartella';
      return 'Spazio';
    };

    // Get add items based on type
    const addItems = useMemo(() => {
      const items: Array<{
        icon: React.ReactNode;
        label: string;
        action: ContextMenuAction['type'];
      }> = [];

      // Everything can contain everything - always show all options
      items.push(
        {
          icon: <FolderPlus className="w-3.5 h-3.5" />,
          label: 'Cartella',
          action: 'add-folder',
        },
        {
          icon: <ListPlus className="w-3.5 h-3.5" />,
          label: 'Task',
          action: 'add-list',
        },
        {
          icon: <FileText className="w-3.5 h-3.5" />,
          label: 'Documento',
          action: 'add-document',
        },
        {
          icon: <MessageSquare className="w-3.5 h-3.5" />,
          label: 'Chat',
          action: 'add-chat',
        },
        {
          icon: <Mic className="w-3.5 h-3.5" />,
          label: 'Canale vocale',
          action: 'add-voice-channel',
        },
        {
          icon: <Table className="w-3.5 h-3.5" />,
          label: 'Foglio di calcolo',
          action: 'add-spreadsheet',
        },
        {
          icon: <PresentationIcon className="w-3.5 h-3.5" />,
          label: 'Presentazione',
          action: 'add-presentation',
        }
      );

      return items;
    }, []);

    const menuItems = useMemo(() => {
      const items: Array<{
        icon: React.ReactNode;
        label: string;
        action: ContextMenuAction['type'] | null;
        danger?: boolean;
        divider?: boolean;
        hasSubmenu?: boolean;
      }> = [];

      // Common actions
      items.push({
        icon: <Edit3 className="w-3.5 h-3.5" />,
        label: 'Rinomina',
        action: 'rename',
      });

      // Add submenu for containers only (not for leaf items)
      if (type !== 'item') {
        items.push({
          icon: <Plus className="w-3.5 h-3.5" />,
          label: 'Aggiungi',
          action: null,
          hasSubmenu: true,
        });
      }

      // Common actions for all
      items.push({
        icon: <Copy className="w-3.5 h-3.5" />,
        label: 'Duplica',
        action: 'duplicate',
        divider: true,
      });

      items.push({
        icon: <Move className="w-3.5 h-3.5" />,
        label: 'Sposta',
        action: 'move',
      });

      // Add "Move to folder" option for lists and folders (not for items or spaces)
      if (type === 'list' || type === 'folder') {
        items.push({
          icon: <Move className="w-3.5 h-3.5" />,
          label: 'Sposta in cartella',
          action: 'move-to-folder',
        });
      }

      items.push({
        icon: <Archive className="w-3.5 h-3.5" />,
        label: 'Archivia',
        action: 'archive',
      });

      // Settings for containers only
      if (type !== 'item') {
        items.push({
          icon: <Settings className="w-3.5 h-3.5" />,
          label: 'Impostazioni',
          action: 'settings',
          divider: true,
        });
      }

      // Danger zone
      items.push({
        icon: <Trash2 className="w-3.5 h-3.5" />,
        label: 'Elimina',
        action: 'delete',
        danger: true,
        divider: true,
      });

      return items;
    }, [type]);

    useClickOutside([submenuRef], () => setShowAddSubmenu(false), showAddSubmenu);

    return (
      <div
        ref={(node) => {
          (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className="fixed z-9999 min-w-[180px] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl overflow-visible animate-scale-in origin-top-left"
        style={{
          left: pos.left,
          top: pos.top,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-border-primary bg-surface-tertiary/30">
          <div className="text-[10px] text-text-quaternary uppercase tracking-wider font-semibold">
            {getTypeLabel()}
          </div>
          <div className="text-[11px] font-medium text-text-primary truncate mt-0.5">
            {getTitle()}
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-1 relative">
          {menuItems.map((item, index) => (
            <div key={item.action || `item-${index}`} className="relative">
              {item.divider && index > 0 && <div className="h-px bg-border-primary my-1 mx-2" />}
              {item.hasSubmenu ? (
                <div
                  className="relative"
                  onMouseEnter={() => setShowAddSubmenu(true)}
                  onMouseLeave={(e) => {
                    // Only close if mouse is not moving to submenu
                    if (!submenuRef.current?.contains(e.relatedTarget as Node)) {
                      setShowAddSubmenu(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAddSubmenu(!showAddSubmenu);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className={clsx(
                      'w-full flex items-center justify-between gap-2.5 px-3 py-2 text-[11px] transition-colors',
                      'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                      showAddSubmenu && 'bg-surface-hover'
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight
                      className={clsx(
                        'w-3 h-3 transition-transform',
                        showAddSubmenu && 'rotate-90'
                      )}
                    />
                  </button>
                  {showAddSubmenu && (
                    <div
                      ref={submenuRef}
                      data-submenu="true"
                      className="absolute top-0 ml-1 min-w-[180px] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl overflow-hidden z-10000"
                      data-submenu-side={pos.left + 180 + 180 > window.innerWidth ? 'left' : 'right'}
                      style={{ ...(pos.left + 180 + 180 > window.innerWidth ? { right: '100%' } : { left: '100%' }), maxHeight: '400px', overflowY: 'auto' as const }}
                      onMouseEnter={() => {
                        setShowAddSubmenu(true);
                      }}
                      onMouseLeave={() => {
                        setShowAddSubmenu(false);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <div className="py-1">
                        {addItems.map((addItem) => (
                          <button
                            key={addItem.action}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onAction(addItem.action);
                              // Delay closing to ensure action completes
                              setTimeout(() => {
                                onClose();
                              }, 100);
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-[11px] transition-colors text-text-secondary hover:bg-surface-hover hover:text-text-primary"
                          >
                            {addItem.icon}
                            <span className="font-medium">{addItem.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (item.action) {
                      onAction(item.action);
                      onClose();
                    }
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-[11px] transition-colors',
                    item.danger
                      ? 'text-accent-rose hover:bg-accent-rose/10'
                      : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                  )}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-3 py-1.5 border-t border-border-primary bg-surface-tertiary/20">
          <div className="text-[9px] text-text-quaternary">
            Premi <kbd className="kbd text-[8px]">Esc</kbd> per chiudere
          </div>
        </div>
      </div>
    );
  }
);

ProjectContextMenu.displayName = 'ProjectContextMenu';
